<template>
  <div class="tone-card" :data-material-id="item.id" :class="{ 'is-us': !isChinese() }">
    <div class="tone-card__wrapper">
      <!-- Tags 插槽 -->
      <div v-if="$slots.tags" class="tone-card__tags">
        <slot name="tags" :item="item" />
      </div>

      <!-- Actions 插槽 -->
      <div v-if="$slots.actions" class="tone-card__actions">
        <slot name="actions" :item="item" :source="source" />
      </div>

      <div class="tone-card__content">
        <div class="tone-card__name">
          <slot name="name" :item="item">
            <span>{{ item.name }}</span>
          </slot>
        </div>
        <div class="tone-card__desc">
          <slot name="desc" :item="item">
            <template v-if="isChinese()" v-for="(tag, index) in tags" :key="index">
              <div>{{ tag }}</div>
              <Divider
                :orientation="'vertical'"
                v-if="index !== tags.length - 1"
                class="tone-card__divider_vertical"
              />
            </template>
            <span v-else>{{ item.subtitle }}</span>
          </slot>
        </div>
      </div>

      <div class="tone-card__right">
        <div class="tone-card__play-group">
          <PlayVoice
            v-if="item.audioUrl"
            :id="`${item.id}-base`"
            :url="item.audioUrl"
            :type="'base'"
          />
          <PlayVoice
            v-if="item.audioProUrl"
            :id="`${item.id}-pro`"
            :url="item.audioProUrl"
            :type="'pro'"
          />
        </div>
      </div>
    </div>

    <div class="tone-card__divider-wrapper" v-if="$slots.footer"></div>

    <div class="tone-card__footer" v-if="$slots.footer">
      <slot name="footer" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { getI18nService } from '@rendora/common/services/i18n/index';
import { Divider } from '@rendora/design/components/divider';
import { computed } from 'vue';
import PlayVoice from '~/design/components/play-voice/index.vue';

const i18nService = getI18nService();
const $t = i18nService.t;

// 纯 UI 组件的 Props 定义
export interface VoiceCardItem {
  // 唯一标识
  id: string | number;
  // 名称
  name: string;
  // 副标题（用于展示语言等信息）
  subtitle?: string;
  // 性别
  gender?: 'Female' | 'Male';
  // 基础音频 URL
  audioUrl?: string;
  // 专业版音频 URL
  audioProUrl?: string;
}

const props = withDefaults(
  defineProps<{
    // 卡片数据项
    item: VoiceCardItem;
    // 额外数据（可选，用于传递给插槽）
    source?: any;
  }>(),
  {
    source: undefined,
  },
);

const gender = computed(() => {
  return props.item.gender === 'Female' ? $t('nv') : $t('nan');
});

const language = computed(() => {
  return props.item.subtitle?.split(' ') || [];
});

const tags = computed(() => {
  return [gender.value, ...language.value];
});
</script>

<style scoped lang="scss">
.tone-card {
  width: 100%;
  min-width: 325px;
  &.is-us {
    min-width: 305px;
  }
  position: relative;
  border: 1px solid var(--x-back-4);
  border-radius: 8px;
  height: 100%;
}

.tone-card__wrapper {
  position: relative;
  display: flex;
  align-items: center;
  padding: 26px 20px;
}

.tone-card:hover {
  box-shadow: var(--x-shadow);
}

.tone-card:hover .tone-card__actions {
  display: flex;
}

.tone-card:hover .tone-card__tags {
  display: none;
}

.tone-card__tags {
  position: absolute;
  top: 10px;
  left: 10px;
}

.tone-card__actions {
  position: absolute;
  top: 8px;
  left: 8px;
  display: none;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
}

.tone-card__right {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.tone-card__play-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tone-card__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  // margin-left: 12px;
  gap: 8px;
  overflow: hidden;
}

.tone-card__name {
  width: 100%;
  font-size: var(--x-text-16-size);
  font-weight: var(--x-weight-medium);
  line-height: var(--x-text-16-line-height);
  color: var(--x-text-1);
  display: flex;
}

.tone-card__desc {
  display: flex;
  width: 100%;
  padding-right: 42px;
  gap: 8px;
  align-items: center;
  color: var(--x-text-5);
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-14-line-height);
}

.tone-card__divider_vertical {
  margin: 0;
  height: 12px;
  transform: translateY(4px);
}

.tone-card__footer {
  display: flex;
  padding: 12px 20px;
}

.tone-card__divider-wrapper {
  margin: 0;
  padding: 0 20px;
  position: relative;
  &::before {
    content: ' ';
    display: block;
    width: 100%;
    height: 1px;
    background-color: var(--x-back-4);
  }
}
</style>
